import React, { useState } from 'react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ArrowUpRight, CalendarIcon } from 'lucide-react';
import { EVENT_TYPES, IMAGES } from '../mock';
import { postBooking } from '../api';
import { logError } from '../lib/logger';
import Reveal from './Reveal';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Calendar } from './ui/calendar';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';

const initialForm = { name: '', email: '', phone: '', eventType: '', date: '', city: '', message: '' };

const fieldClass =
  'h-auto w-full rounded-none border-0 border-b border-white/20 bg-transparent px-0 py-3 text-base tracking-[0.03em] text-neutral-100 shadow-none transition-colors duration-300 placeholder:text-neutral-500 focus-visible:ring-0 focus-visible:border-gold';

const labelClass = 'text-[10px] uppercase tracking-[0.3em] text-neutral-400';

const today = () => new Date(new Date().setHours(0, 0, 0, 0));

const validateBooking = (form) => {
  const required = [form.name.trim(), form.email.trim(), form.eventType, form.date];
  if (required.some((v) => !v)) return 'Please fill in your name, email, event type and preferred date.';
  if (!/^\S+@\S+\.\S+$/.test(form.email)) return 'Please enter a valid email address.';
  return null;
};

const Field = ({ id, label, className = '', children }) => (
  <div className={`space-y-2.5 ${className}`}>
    <Label htmlFor={id} className={labelClass}>
      {label}
    </Label>
    {children}
  </div>
);

const TextField = ({ id, label, value, onChange, placeholder, type = 'text' }) => (
  <Field id={id} label={label}>
    <Input id={id} type={type} value={value} onChange={onChange} placeholder={placeholder} className={fieldClass} />
  </Field>
);

const EventTypeField = ({ value, onChange }) => (
  <Field label="Event Type">
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`${fieldClass} flex items-center justify-between data-[placeholder]:text-neutral-500`}>
        <SelectValue placeholder="Select event type" />
      </SelectTrigger>
      <SelectContent className="border-white/10 bg-ink text-neutral-200">
        {EVENT_TYPES.map((t) => (
          <SelectItem key={t} value={t} className="focus:bg-white/10 focus:text-gold">
            {t}s
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </Field>
);

const DateField = ({ value, onChange }) => {
  const selected = value ? new Date(`${value}T00:00:00`) : undefined;
  return (
    <Field label="Preferred Date">
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={`${fieldClass} flex items-center justify-between text-left ${selected ? '' : 'text-neutral-500'}`}
          >
            {selected ? format(selected, 'dd/MM/yyyy') : 'dd/mm/yyyy'}
            <CalendarIcon size={15} className="text-neutral-500" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto border-white/10 bg-cream p-0 text-ink">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(d) => onChange(d ? format(d, 'yyyy-MM-dd') : '')}
            disabled={(d) => d < today()}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </Field>
  );
};

const SubmitButton = ({ submitting }) => (
  <div className="sm:col-span-2">
    <button
      type="submit"
      disabled={submitting}
      className="group inline-flex items-center gap-3 bg-gold px-9 py-4 text-[11px] tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-gold-light disabled:opacity-60"
    >
      {submitting ? 'SENDING...' : 'SEND A BOOKING REQUEST'}
      <ArrowUpRight
        size={13}
        className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
      />
    </button>
  </div>
);

const BookingIntro = () => (
  <>
    <Reveal>
      <p className="mb-6 text-[10px] uppercase tracking-[0.5em] text-gold">Booking</p>
    </Reveal>
    <Reveal delay={120}>
      <h2 className="font-serif text-3xl font-light leading-[1.12] tracking-[0.03em] text-white md:text-4xl xl:text-[2.9rem]">
        PLAN YOUR NEXT
        <br />
        KHM EVENT
      </h2>
    </Reveal>
    <Reveal delay={220}>
      <p className="mt-6 flex flex-wrap items-center gap-y-2 text-[10px] tracking-[0.18em] text-neutral-400">
        {EVENT_TYPES.map((t, i) => (
          <React.Fragment key={t}>
            {i > 0 && <span className="mx-3 text-neutral-600">|</span>}
            <span>{t}s</span>
          </React.Fragment>
        ))}
      </p>
    </Reveal>
  </>
);

const BookingImage = () => (
  <div className="relative min-h-[45vh] overflow-hidden lg:min-h-0">
    <img
      src={IMAGES.booking}
      alt="Vintage microphone on stage"
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 ease-out hover:scale-[1.03]"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-ink/20 lg:bg-gradient-to-r lg:from-transparent lg:to-ink/20" />
  </div>
);

const BookingForm = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const setValue = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const setInput = (key) => (e) => setValue(key)(e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validateBooking(form);
    if (error) {
      toast.error(error);
      return;
    }
    setSubmitting(true);
    try {
      await postBooking(form);
      setForm(initialForm);
      toast.success('Booking request sent', {
        description: "Kanwal's management team will get back to you within 48 hours.",
      });
    } catch (err) {
      logError('Booking submission failed:', err);
      toast.error('Could not send your request', {
        description: 'Please check your connection and try again in a moment.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-11 grid gap-x-10 gap-y-9 sm:grid-cols-2">
      <TextField id="bf-name" label="Your Name" value={form.name} onChange={setInput('name')} placeholder="Full name" />
      <TextField
        id="bf-email"
        type="email"
        label="Email"
        value={form.email}
        onChange={setInput('email')}
        placeholder="you@example.com"
      />
      <TextField id="bf-phone" label="Phone" value={form.phone} onChange={setInput('phone')} placeholder="+92 300 0000000" />
      <EventTypeField value={form.eventType} onChange={setValue('eventType')} />
      <DateField value={form.date} onChange={setValue('date')} />
      <TextField
        id="bf-city"
        label="City / Venue"
        value={form.city}
        onChange={setInput('city')}
        placeholder="Where is it happening?"
      />
      <Field id="bf-message" label="Tell us about the event" className="sm:col-span-2">
        <Textarea
          id="bf-message"
          value={form.message}
          onChange={setInput('message')}
          placeholder="Audience size, timings, anything else we should know"
          rows={3}
          className={`${fieldClass} resize-none`}
        />
      </Field>
      <SubmitButton submitting={submitting} />
    </form>
  );
};

const Booking = () => (
  <section id="booking" className="relative bg-ink">
    <div className="grid lg:grid-cols-2">
      <BookingImage />
      <div className="relative overflow-hidden px-6 py-12 md:px-14 md:py-20 xl:px-20">
        <div className="absolute inset-0">
          <img
            src={IMAGES.live}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="h-full w-full object-cover object-center opacity-[0.12]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/70 to-ink" />
        </div>
        <div className="relative z-10">
          <BookingIntro />
          <Reveal delay={320}>
            <BookingForm />
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

export default Booking;
